import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { LogoWord } from "../components/Brand";

function formatErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function RegisterPage() {
  const { loading: authLoading, isAuthenticated, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  if (authLoading) {
    return <div className="pt-40 text-center text-muted-foreground" data-testid="register-auth-loading">Loading…</div>;
  }

  if (isAuthenticated) return <Navigate to="/portal" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Check your inbox to verify your email.");
      nav("/portal");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden" data-testid="register-page">
      <div className="hidden lg:block absolute inset-y-0 left-0 w-1/2 aurora-glow opacity-70 pointer-events-none" />
      <div className="flex items-center justify-center p-10">
        <motion.form
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6 p-8 clip-frame border border-border bg-background"
        >
          <LogoWord />
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Open a Client Account</div>
            <h1 className="display display-section text-4xl leading-tight mt-2">Let&apos;s begin.</h1>
          </div>
          <div className="space-y-3">
            <Input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="register-name" className="rounded-sm h-12" />
            <Input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} data-testid="register-email" className="rounded-sm h-12" />
            <Input placeholder="Company" value={form.company} onChange={set("company")} data-testid="register-company" className="rounded-sm h-12" />
            <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="register-phone" className="rounded-sm h-12" />
            <Input required type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} data-testid="register-password" className="rounded-sm h-12" />
          </div>
          <Button type="submit" disabled={loading} className="btn-gold rounded-sm w-full h-12 uppercase font-mono-2 text-xs tracking-widest" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
          <div className="text-sm text-muted-foreground text-center">
            Already registered? <Link to="/login" className="text-[color:var(--gold)] underline">Sign in</Link>
          </div>
        </motion.form>
      </div>
      <div className="hidden lg:flex flex-col justify-between p-14 border-l border-border bg-secondary/40">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">B2B · Onboarding</div>
        <div>
          <div className="chapter-num">01</div>
          <h2 className="display display-section text-5xl leading-[0.9] tracking-[-0.03em] mt-6">
            One account. <em className="italic text-[color:var(--gold)]">One workshop.</em>
          </h2>
          <p className="mt-6 text-steel-text max-w-md">Quote requests, order tracking, drawings and AMC — inside one clean workspace.</p>
        </div>
        <div className="text-xs text-muted-foreground">© DEBSUN 2026 — Intelligence in Motion</div>
      </div>
    </div>
  );
}
